import React, { useState } from 'react';
import { User, Globe, Zap, Clock, Heart, Leaf, X, Home, Package, Gift, Star, HelpCircle, Mail, ChevronRight, ChevronDown, Scale } from 'lucide-react';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export const Header = ({ 
  scrollY, 
  theme, 
  language, 
  setLanguage, 
  user, 
  setShowAuth, 
  setShowDashboard, 
  fetchDashboard,
  menuOpen,
  setMenuOpen,
  t 
}) => {
  return (
    <header 
      className="fixed top-0 md:top-8 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        background: scrollY > 50 ? 'rgba(5,5,5,0.98)' : 'rgba(5,5,5,0.8)', 
        backdropFilter: 'blur(20px)',
        borderBottom: scrollY > 50 ? `1px solid ${theme.border}` : 'none'
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_37cca134-41a5-49ca-9252-6c0a7dd12fc1/artifacts/rsel26pf_Fancy_Donuts_Wortmarke.png" 
            alt="Fancy Donuts" 
            className="h-8 sm:h-10 w-auto"
            style={{ filter: 'brightness(1.1)' }}
          />
        </button>
        
        {/* Trust Icons - nur auf Desktop */}
        <div className="hidden lg:flex items-center gap-6 text-xs" style={{ color: theme.textMuted }}>
          <span className="flex items-center gap-2 trust-icon cursor-default" data-testid="trust-icon-express">
            <Zap className="w-4 h-4 trust-icon-svg" style={{ color: BRAND.turquoise }} />{t.nav.expressDelivery}
          </span>
          <span className="flex items-center gap-2 trust-icon cursor-default" data-testid="trust-icon-fresh">
            <Clock className="w-4 h-4 trust-icon-svg" style={{ color: BRAND.pink }} />{t.nav.freshDaily}
          </span>
          <span className="flex items-center gap-2 trust-icon cursor-default" data-testid="trust-icon-handmade">
            <Heart className="w-4 h-4 trust-icon-svg" style={{ color: BRAND.yellow }} />{t.nav.handmade}
          </span>
          <span className="flex items-center gap-2 trust-icon cursor-default" data-testid="trust-icon-sustainable">
            <Leaf className="w-4 h-4 trust-icon-svg" style={{ color: '#4ade80' }} />{t.nav.sustainable}
          </span>
        </div>
        
        {/* Right Side */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language Toggle */}
          <button 
            onClick={() => setLanguage(language === 'de' ? 'en' : 'de')} 
            className="group w-11 h-11 rounded-xl flex items-center justify-center gap-1.5 transition-all hover:scale-105"
            style={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.8)' }} />
            <span className="uppercase text-[10px] font-black" style={{ color: 'rgba(255,255,255,0.8)' }}>{language}</span>
          </button>
          
          {/* User/Login Button */}
          {user ? (
            <button 
              onClick={() => { setShowDashboard(true); fetchDashboard(); }} 
              className="group w-11 h-11 rounded-xl flex items-center justify-center transition-all hover:scale-105" 
              style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}
              data-testid="user-dashboard-btn"
            >
              <User className="w-5 h-5 transition-transform group-hover:scale-110" style={{ color: 'rgba(255,255,255,0.8)' }} />
            </button>
          ) : (
            <button 
              onClick={() => setShowAuth(true)} 
              className="group w-11 h-11 rounded-xl flex items-center justify-center transition-all hover:scale-105" 
              style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}
              data-testid="login-btn"
            >
              <User className="w-5 h-5 transition-transform group-hover:scale-110" style={{ color: 'rgba(255,255,255,0.8)' }} />
            </button>
          )}
          
          {/* Burger Menu Button */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)} 
            className="group w-11 h-11 rounded-xl flex flex-col items-center justify-center gap-1.5 transition-all hover:scale-105"
            style={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
              border: '1px solid rgba(255,255,255,0.15)'
            }}
            data-testid="burger-menu-btn"
          >
            <span className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} 
              style={{ backgroundColor: BRAND.turquoise }} />
            <span className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} 
              style={{ backgroundColor: BRAND.pink }} />
            <span className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} 
              style={{ backgroundColor: BRAND.yellow }} />
          </button>
        </div>
      </nav>
    </header>
  );
};

// Aufklappbares Rechtliches-Untermenü
const LegalSubmenu = ({ legalItems, setMenuOpen, setLegalPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-4 p-4 rounded-xl transition-all hover:scale-[1.02] group"
        style={{ 
          background: isOpen ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${isOpen ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)'}`
        }}
        data-testid="menu-item-legal"
      >
        <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all group-hover:scale-110"
          style={{ 
            background: 'linear-gradient(135deg, rgba(156,163,175,0.2), rgba(107,114,128,0.15))',
            border: '1px solid rgba(156,163,175,0.3)'
          }}>
          <Scale className="w-5 h-5" style={{ color: '#9ca3af' }} />
        </div>
        <span className="font-semibold text-white">Rechtliches</span>
        <ChevronDown 
          className={`w-4 h-4 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          style={{ color: 'rgba(255,255,255,0.4)' }} 
        />
      </button>
      
      {/* Untermenü Items */}
      <div 
        className={`overflow-hidden transition-all duration-200 ease-out ${isOpen ? 'max-h-60 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
      >
        <div className="pl-4 space-y-1">
          {legalItems.map(item => (
            <button
              key={item.page}
              onClick={() => { 
                setMenuOpen(false); 
                setLegalPage(item.page); 
                window.scrollTo(0, 0); 
              }}
              className="w-full flex items-center gap-3 p-3 rounded-lg text-sm font-medium transition-all hover:scale-[1.01] hover:bg-white/5"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              data-testid={`legal-item-${item.page}`}
            >
              <ChevronRight className="w-3 h-3" style={{ color: BRAND.turquoise }} />
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SlideOutMenu = ({ 
  menuOpen, 
  setMenuOpen, 
  theme, 
  scrollToConfigurator, 
  setLegalPage 
}) => {
  const menuItems = [
    { icon: Home, label: 'Startseite', section: 'hero' },
    { icon: Package, label: 'Donut-Abo', section: 'configurator' },
    { icon: Gift, label: 'Catering', section: 'catering' },
    { icon: Star, label: 'Vorteile', section: 'benefits' },
  ];

  const legalItems = [
    { label: 'Impressum', page: 'impressum' },
    { label: 'Datenschutz', page: 'datenschutz' },
    { label: 'Widerruf', page: 'widerruf' },
    { label: 'AGB', page: 'agb' },
  ];

  return (
    <>
      {/* Slide-Out Menu */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 z-[60] transform transition-transform duration-200 ease-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ 
          background: 'linear-gradient(180deg, rgba(15,15,20,0.98) 0%, rgba(10,10,15,0.99) 100%)',
          backdropFilter: 'blur(20px)',
          borderLeft: `1px solid ${BRAND.turquoise}20`,
          boxShadow: menuOpen ? '-10px 0 50px rgba(0,0,0,0.5)' : 'none'
        }}
      >
        {/* Menu Header */}
        <div className="p-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="flex items-center justify-between">
            <span className="text-xl font-black" style={{ 
              background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Menü</span>
            <button 
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-105"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              <X className="w-5 h-5" style={{ color: BRAND.pink }} />
            </button>
          </div>
        </div>
        
        {/* Menu Items */}
        <div className="p-4 space-y-2">
          {menuItems.map((item, index) => (
            <button
              key={item.section}
              onClick={() => {
                setMenuOpen(false);
                setTimeout(() => {
                  if (item.section === 'hero') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else if (item.section === 'configurator') {
                    scrollToConfigurator('abo');
                  } else if (item.section === 'catering') {
                    scrollToConfigurator('catering');
                  } else {
                    const el = document.querySelector(`[data-section="${item.section}"]`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }, 300);
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl transition-all hover:scale-[1.02] group"
              style={{ 
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.05)',
                animationDelay: `${index * 50}ms`
              }}
              data-testid={`menu-item-${item.section}`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all group-hover:scale-110"
                style={{ 
                  background: `linear-gradient(135deg, ${BRAND.turquoise}20, ${BRAND.pink}15)`,
                  border: `1px solid ${BRAND.turquoise}30`
                }}>
                <item.icon className="w-5 h-5" style={{ color: BRAND.turquoise }} />
              </div>
              <span className="font-semibold text-white">{item.label}</span>
              <ChevronRight className="w-4 h-4 ml-auto transition-transform group-hover:translate-x-1" style={{ color: 'rgba(255,255,255,0.4)' }} />
            </button>
          ))}
          
          {/* Rechtliches - Aufklappbares Untermenü */}
          <LegalSubmenu legalItems={legalItems} setMenuOpen={setMenuOpen} setLegalPage={setLegalPage} />
        </div>
        
        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="flex items-center justify-center gap-4">
            <a href="#" className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              {/* Instagram */}
              <svg className="w-5 h-5" fill="currentColor" style={{ color: 'rgba(255,255,255,0.6)' }} viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
              </svg>
            </a>
            {/* TikTok */}
            <a href="#" className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              <svg className="w-5 h-5" fill="currentColor" style={{ color: 'rgba(255,255,255,0.6)' }} viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            {/* Facebook */}
            <a href="#" className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              <svg className="w-5 h-5" fill="currentColor" style={{ color: 'rgba(255,255,255,0.6)' }} viewBox="0 0 24 24">
                <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
              </svg>
            </a>
          </div>
          <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.3)' }}>
            © 2026 Fancy Donuts
          </p>
        </div>
      </div>
      
      {/* Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
};

export const AnnouncementBanner = () => {
  return (
    <div className="hidden md:block fixed top-0 left-0 right-0 z-40 overflow-hidden" 
      style={{ 
        background: 'linear-gradient(90deg, #0a0a0a 0%, #111 50%, #0a0a0a 100%)',
        borderBottom: '1px solid rgba(129, 203, 214, 0.15)',
        height: '32px'
      }}>
      <div className="animate-scroll-x flex items-center h-full whitespace-nowrap">
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>⭐ 500+ zufriedene Kunden</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>🚚 Express 24h</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>💼 15% Abo-Rabatt</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>🍩 GRATIS LIEFERUNG ab 50€</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>⭐ 500+ zufriedene Kunden</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>🚚 Express 24h</span>
        <span className="mx-6 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>💼 15% Abo-Rabatt</span>
      </div>
    </div>
  );
};

export default Header;
